import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  XMarkIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const Staff = () => {
  const [staffData, setStaffData] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  // Removed checkImageExists - using direct loading with error handling for better performance

  // Extract staff name from filename and organize by department folder
  const processStaffData = (filename, department) => {
    // Use filename (without extension) as the staff member's name
    const cleanName = filename.replace('.png', '');

    return {
      id: `${department}-${filename.replace('.png', '')}`,
      name: cleanName,
      filename: filename,
      department: department,
      image: `/assets/documents/staff/${department}/${filename}`
    };
  };

  // Load staff data
  useEffect(() => {
    const loadStaffData = () => {
      try {
        // Define all department folders and their staff members
        const departmentData = {
          '2nd Grade': [
            'Adam Newman.png',
            'Brenda Morales.png',
            'Deidre Williams-Allen.png',
            'Kim Steimke.png',
            'Tameka Williams.png'
          ],
          '3K:Pre-K': [
            'Caitlin McQueen.png',
            'Denise Hughes-Graff.png',
            'Isabel Vera-Calderon.png',
            'Toya Kelly.png'
          ],
          '3rd Grade': [
            'Emma March.png',
            'Mayra Mendez.png'
          ],
          '4th Grade': [
            'Kelly Cheek-Powell.png',
            'Robert Simpson.png'
          ],
          '5th Grade': [
            'Alicia Kaplan.png',
            'Irma Vasquez.png',
            'Luz Rivera-Garcia.png',
            'Tricia Narine.png'
          ],
          'Educational Assistants': [
            'Ana Novas.png',
            'Joy Oroyo.png',
            'Mayra Garay-Cruz.png'
          ],
          'Enrichment': [
            'Curtis Widem.png',
            'George Tsesmelis.png',
            'Michael Salvatore.png'
          ],
          'First Grade': [
            'Ana Pena-Velez.png',
            'Emeliza Cruz.png',
            'Hande Williams.png',
            'Rebecca Gonzaga.png',
            'Shakira Santiago.png'
          ],
          'IEP Team': [
            // Empty for now - will show department even with no staff
          ],
          'Kindergarten': [
            'Ana Taveras.png',
            'Luisana Roman.png',
            'Manana Ninua.png',
            'Marilyn Martinez.png'
          ],
          'Related Service Providers': [
            'Krysteena Alloggio.png',
            'Michelle Eis.png'
          ],
          'Social Worker': [
            'Joann Nguyen.png'
          ],
          'Special Providers': [
            'Aqueisha Jeremiah.png',
            'Kristin Boyar.png'
          ]
        };

        // Process all staff files by department
        const allStaff = [];
        Object.entries(departmentData).forEach(([department, files]) => {
          files.forEach(file => {
            allStaff.push(processStaffData(file, department));
          });
        });

        // Sort alphabetically by name
        allStaff.sort((a, b) => a.name.localeCompare(b.name));
        
        setStaffData(allStaff);
        setLoading(false);
      } catch (error) {
        console.error('Error loading staff data:', error);
        setLoading(false);
      }
    };

    loadStaffData();
  }, []);

  // Section orders
  const gradeOrder = [
    '3K:Pre-K',
    'Kindergarten',
    'First Grade',
    '2nd Grade',
    '3rd Grade',
    '4th Grade',
    '5th Grade'
  ];
  const otherOrder = [
    'Educational Assistants',
    'Enrichment',
    'IEP Team',
    'Related Service Providers',
    'Social Worker',
    'Special Providers'
  ];

  const getStaffByDepartment = (department) =>
    staffData.filter(member => member.department === department);

  if (loading) {
    return (
      <main id="main-content" className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#630503] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading staff directory...</p>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main id="main-content" className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Our Team
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                Staff <span className="font-bold">Directory</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Get to know the passionate educators and staff members who make P.S. 11X The Highbridge School 
                a place where every student can thrive and reach their full potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Directory Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-6 shadow-lg">
              Faculty Directory
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 leading-tight">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Browse by grade and department. Click any card to view the staff bio.
            </p>
          </div>
        </div>
      </section>

      {/* Staff by Sections */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {staffData.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Staff directory unavailable</h3>
              <p className="text-gray-600">Please check back later.</p>
            </div>
          ) : (
            <div className="space-y-16">
              {/* Grades */}
              {gradeOrder.map((dept) => {
                const members = getStaffByDepartment(dept);
                return (
                  <div key={dept}>
                    <div className="flex items-baseline justify-between mb-6">
                      <h3 className="text-2xl font-bold text-gray-900">{dept}</h3>
                      <span className="text-sm text-gray-500">{members.length} {members.length === 1 ? 'member' : 'members'}</span>
                    </div>
                    {members.length === 0 ? (
                      <div className="text-gray-500 italic">Coming soon.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {members.map((staff) => (
                          <div
                            key={staff.id}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${staff.name} bio`}
                            className="group cursor-pointer bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden"
                            onClick={() => setSelectedStaff(staff)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedStaff(staff); } }}
                          >
                            <div className="p-6 bg-[#630503]">
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <img
                                  src={staff.image}
                                  alt={`${staff.name} Bio`}
                                  className="w-full h-48 object-cover object-top"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.src = '/assets/logo.png';
                                    e.target.alt = `${staff.name} Bio (Image not available)`;
                                  }}
                                />
                              </div>
                            </div>

                            <div className="p-6">
                              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#630503] transition-colors">
                                {staff.name}
                              </h3>
                              <p className="text-sm text-gray-600 mb-4">{staff.department}</p>

                              <div className="flex items-center text-xs text-[#630503] font-medium">
                                <span>Click to view full bio</span>
                                <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Other Departments */}
              {otherOrder.map((dept) => {
                const members = getStaffByDepartment(dept);
                return (
                  <div key={dept}>
                    <div className="flex items-baseline justify-between mb-6">
                      <h3 className="text-2xl font-bold text-gray-900">{dept}</h3>
                      <span className="text-sm text-gray-500">{members.length} {members.length === 1 ? 'member' : 'members'}</span>
                    </div>
                    {members.length === 0 ? (
                      <div className="text-gray-500 italic">Coming soon.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {members.map((staff) => (
                          <div
                            key={staff.id}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${staff.name} bio`}
                            className="group cursor-pointer bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden"
                            onClick={() => setSelectedStaff(staff)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedStaff(staff); } }}
                          >
                            <div className="p-6 bg-[#630503]">
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <img
                                  src={staff.image}
                                  alt={`${staff.name} Bio`}
                                  className="w-full h-48 object-cover object-top"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.src = '/assets/logo.png';
                                    e.target.alt = `${staff.name} Bio (Image not available)`;
                                  }}
                                />
                              </div>
                            </div>

                            <div className="p-6">
                              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#630503] transition-colors">
                                {staff.name}
                              </h3>
                              <p className="text-sm text-gray-600 mb-4">{staff.department}</p>

                              <div className="flex items-center text-xs text-[#630503] font-medium">
                                <span>Click to view full bio</span>
                                <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bio Modal */}
      {selectedStaff && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="staff-modal-title"
          onClick={() => setSelectedStaff(null)}
        >
          <div
            className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#630503] text-white px-6 py-4 flex justify-between items-center">
              <h3 id="staff-modal-title" className="text-lg font-semibold">{selectedStaff.name}</h3>
              <button
                onClick={() => setSelectedStaff(null)}
                aria-label="Close staff bio"
                className="text-white hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 max-h-[80vh] overflow-auto">
              <img 
                src={selectedStaff.image}
                alt={`${selectedStaff.name} Bio`}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '70vh' }}
                onError={(e) => {
                  e.target.src = '/assets/logo.png';
                  e.target.alt = `${selectedStaff.name} Bio (Image not available)`;
                }}
              />
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {selectedStaff.department} - {selectedStaff.name}
                </p>
                <button
                  onClick={() => setSelectedStaff(null)}
                  aria-label="Close staff bio"
                  className="bg-[#630503] text-white px-4 py-2 rounded-lg hover:bg-[#630503]/90 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section className="py-20 bg-[#630503]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              Questions About <span className="font-bold">Our Staff?</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-12">
              Contact our main office for information about staff members, department contacts, or scheduling meetings with our educational team.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:718-329-8000" className="bg-white text-[#630503] hover:bg-white/90 font-semibold px-8 py-4 text-base transition-colors rounded-lg inline-block">
                Call (718) 329-8000
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Staff; 